import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Plus, RefreshCw, Printer, Search, Filter, Edit2, AlertOctagon } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../services/api';

export default function InternalScrapPage() {
  const [items, setItems] = useState([]);
  const [defectTypes, setDefectTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [productionLots, setProductionLots] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [defectFilter, setDefectFilter] = useState('all');

  // Dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm());

  function defaultForm() {
    return {
      scrap_date: new Date().toISOString().slice(0, 10),
      customer_id: '', product_id: '', production_lot_id: '',
      scrap_quantity: '', reworked_quantity: '',
      macro_scrap_category: '',
      defect_description: '',
      defect_type_id: '',
      department: '', reported_by: '', notes: '',
    };
  }

  const MACRO_CATEGORIES = ['Scarti grezzi lavorati', 'Scarti grezzi', 'Scarti di lavorazione'];
  const MACRO_COLORS = { 'Scarti grezzi lavorati': '#f97316', 'Scarti grezzi': '#dc2626', 'Scarti di lavorazione': '#3b82f6' };
  const [kpi, setKpi] = useState(null);
  const [macroFilter, setMacroFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (customerFilter !== 'all') params.customer_id = customerFilter;
      if (productFilter !== 'all') params.product_id = productFilter;
      if (defectFilter !== 'all') params.defect_type_id = defectFilter;
      const r = await api.get('/internal-scrap', { params });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento scarti');
    } finally { setLoading(false); }
  }, [customerFilter, productFilter, defectFilter]);

  useEffect(() => {
    load();
    api.get('/internal-scrap/defect-types').then(r => setDefectTypes(r.data || [])).catch(() => {});
    api.get('/clients', { params: { limit: 500 } }).then(r => setClients(r.data?.items || r.data || []));
    api.get('/products', { params: { limit: 500 } }).then(r => setProducts(r.data?.items || r.data || []));
    api.get('/production-lots', { params: { limit: 500 } }).then(r => setProductionLots(r.data?.items || r.data || [])).catch(() => setProductionLots([]));
    api.get('/internal-scrap/kpi/summary').then(r => setKpi(r.data)).catch(() => {});
  }, [load]);

  useEffect(() => {
    api.get('/internal-scrap/kpi/summary').then(r => setKpi(r.data)).catch(() => {});
  }, [items]);

  const openNew = () => { setForm(defaultForm()); setEditingId(null); setOpenDialog(true); };

  const openEdit = (s) => {
    setForm({
      scrap_date: s.scrap_date || '',
      customer_id: s.customer_id || '',
      product_id: s.product_id || '',
      production_lot_id: s.production_lot_id || '',
      scrap_quantity: s.scrap_quantity ?? '',
      reworked_quantity: s.reworked_quantity ?? '',
      macro_scrap_category: s.macro_scrap_category || '',
      defect_description: s.defect_description || '',
      defect_type_id: s.defect_type_id || '',
      department: s.department || '',
      reported_by: s.reported_by || '',
      notes: s.notes || '',
    });
    setEditingId(s.id);
    setOpenDialog(true);
  };

  const save = async () => {
    if (!form.customer_id) return toast.error('Cliente obbligatorio');
    if (!form.product_id) return toast.error('Prodotto obbligatorio');
    if (!form.scrap_date) return toast.error('Data obbligatoria');
    if (!form.macro_scrap_category) return toast.error('Macro tipologia obbligatoria');
    if (!(form.defect_description || '').trim()) return toast.error('Descrizione difetto obbligatoria');
    const sq = Number(form.scrap_quantity);
    if (!sq || sq <= 0) return toast.error('Quantità scartata > 0');
    const rq = Number(form.reworked_quantity || 0);
    if (rq < 0 || rq > sq) return toast.error('Quantità rilavorata non valida');
    const payload = {
      ...form,
      production_lot_id: form.production_lot_id || null,
      defect_type_id: form.defect_type_id || null,
      scrap_quantity: sq, reworked_quantity: rq,
    };
    try {
      if (editingId) {
        await api.put(`/internal-scrap/${editingId}`, payload);
        toast.success('Scarto aggiornato');
      } else {
        await api.post('/internal-scrap', payload);
        toast.success('Scarto registrato');
      }
      setOpenDialog(false); setEditingId(null); setForm(defaultForm());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore salvataggio'); }
  };

  const deleteRow = async (s) => {
    if (!window.confirm(`Eliminare scarto ${s.scrap_code}?`)) return;
    try {
      await api.delete(`/internal-scrap/${s.id}`);
      toast.success('Eliminato');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const printPdf = (s) => {
    const url = `${api.defaults.baseURL}/internal-scrap/${s.id}/pdf`;
    const token = localStorage.getItem('evotech_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => window.open(URL.createObjectURL(b), '_blank'));
  };

  const filtered = items.filter(s => {
    if (macroFilter !== 'all' && s.macro_scrap_category !== macroFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.scrap_code || '').toLowerCase().includes(q)
      || (s.customer_name || '').toLowerCase().includes(q)
      || (s.product_code || '').toLowerCase().includes(q)
      || (s.product_name || '').toLowerCase().includes(q)
      || (s.macro_scrap_category || '').toLowerCase().includes(q)
      || (s.defect_description || '').toLowerCase().includes(q)
      || (s.production_lot_number || '').toLowerCase().includes(q);
  });

  const totalScrap = filtered.reduce((a, s) => a + Number(s.scrap_quantity || 0), 0);
  const totalRew = filtered.reduce((a, s) => a + Number(s.reworked_quantity || 0), 0);

  return (
    <>
      <div className="space-y-4" data-testid="internal-scrap-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertOctagon className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Registro Scarti Interni</h1>
              <p className="text-sm text-slate-500">Registrazione scarti interni per cliente / prodotto / difetto — separato dalle NC formali</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
            <Button onClick={openNew} data-testid="new-scrap-btn"><Plus className="w-4 h-4 mr-2" /> Nuovo scarto</Button>
          </div>
        </div>

        {/* Dashboard */}
        {kpi && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <Card className="border-slate-200"><CardContent className="p-3">
              <div className="text-[10px] uppercase text-slate-500">Totale scarti</div>
              <div className="text-2xl font-bold text-slate-900">{kpi.total_scrap}</div>
              <div className="text-xs text-slate-400">{kpi.records} registrazioni</div>
            </CardContent></Card>
            {MACRO_CATEGORIES.map(m => (
              <Card key={m} style={{ borderColor: MACRO_COLORS[m] }} className="border-2"><CardContent className="p-3">
                <div className="text-[10px] uppercase" style={{ color: MACRO_COLORS[m] }}>{m}</div>
                <div className="text-2xl font-bold" style={{ color: MACRO_COLORS[m] }}>{kpi.by_macro_category?.[m] || 0}</div>
              </CardContent></Card>
            ))}
            <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-3">
              <div className="text-[10px] uppercase text-emerald-600">Rilavorati</div>
              <div className="text-2xl font-bold text-emerald-700">{kpi.total_reworked}</div>
            </CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-3">
              <div className="text-[10px] uppercase text-slate-500">Top difetto</div>
              <div className="text-xs font-semibold text-slate-800 truncate">{kpi.by_defect_description?.[0]?.description || '—'}</div>
              <div className="text-lg font-bold text-red-700">{kpi.by_defect_description?.[0]?.quantity || 0}</div>
            </CardContent></Card>
          </div>
        )}

        {/* Totals riepilogo filtrato */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <Card className="border-slate-200"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-slate-500">Record filtrati</div>
            <div className="text-2xl font-semibold">{filtered.length}</div>
          </CardContent></Card>
          <Card className="border-red-200 bg-red-50"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-red-600">Totale scartato (filtro)</div>
            <div className="text-2xl font-bold text-red-700">{totalScrap}</div>
          </CardContent></Card>
          <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-emerald-600">Totale rilavorato (filtro)</div>
            <div className="text-2xl font-bold text-emerald-700">{totalRew}</div>
          </CardContent></Card>
        </div>

        {/* Filters */}
        <Card><CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cerca codice, cliente, prodotto, difetto…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="scrap-search" />
          </div>
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={productFilter} onValueChange={setProductFilter}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Prodotto" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i prodotti</SelectItem>
              {products.map(p => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={defectFilter} onValueChange={setDefectFilter}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Difetto" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i difetti</SelectItem>
              {defectTypes.map(d => <SelectItem key={d.id} value={d.id}>{d.defect_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={macroFilter} onValueChange={setMacroFilter}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Macro tipologia" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le macro tipologie</SelectItem>
              {MACRO_CATEGORIES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent></Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium text-slate-600">Codice</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Data</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Cliente</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Prodotto</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Lotto</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Macro tipologia</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Descrizione difetto</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">Scartata</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">Rilavorata</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Reparto</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.length === 0 && !loading && <tr><td colSpan={11} className="text-center text-slate-400 py-8">Nessuno scarto registrato.</td></tr>}
                  {filtered.map(s => (
                    <tr key={s.id} className="hover:bg-slate-50" data-testid={`scrap-row-${s.id}`}>
                      <td className="px-3 py-2 font-mono font-semibold">{s.scrap_code}</td>
                      <td className="px-3 py-2 font-mono text-xs">{s.scrap_date}</td>
                      <td className="px-3 py-2">{s.customer_name || '—'}</td>
                      <td className="px-3 py-2">{s.product_code ? `${s.product_code} · ${s.product_name}` : '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{s.production_lot_number || '—'}</td>
                      <td className="px-3 py-2">
                        {s.macro_scrap_category && (
                          <Badge className="text-[10px] border" style={{ borderColor: MACRO_COLORS[s.macro_scrap_category], color: MACRO_COLORS[s.macro_scrap_category] }}>
                            {s.macro_scrap_category}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">{s.defect_description || '—'}</td>
                      <td className="px-3 py-2 text-right font-bold text-red-700">{s.scrap_quantity}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{s.reworked_quantity || 0}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{s.department || '—'}</td>
                      <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => printPdf(s)}><Printer className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(s)}><Edit2 className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteRow(s)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-3xl" data-testid="scrap-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica scarto' : 'Nuovo scarto interno'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><Label>Data *</Label><Input type="date" value={form.scrap_date} onChange={e => setForm({ ...form, scrap_date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Cliente *</Label>
              <Select value={form.customer_id} onValueChange={v => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="scrap-customer"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Prodotto *</Label>
              <Select value={form.product_id} onValueChange={v => setForm({ ...form, product_id: v })}>
                <SelectTrigger data-testid="scrap-product"><SelectValue placeholder="Seleziona prodotto" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Lotto / Commessa</Label>
              <Select value={form.production_lot_id} onValueChange={v => setForm({ ...form, production_lot_id: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{productionLots.map(l => <SelectItem key={l.id} value={l.id}>{l.lot_number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Q.tà scartata *</Label><Input type="number" min="1" value={form.scrap_quantity} onChange={e => setForm({ ...form, scrap_quantity: e.target.value })} data-testid="scrap-qty" /></div>
            <div><Label>Q.tà rilavorata</Label><Input type="number" min="0" value={form.reworked_quantity} onChange={e => setForm({ ...form, reworked_quantity: e.target.value })} /></div>
            <div><Label>Reparto / Area</Label><Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} placeholder="Es: Reparto CNC" /></div>
            <div className="col-span-3"><Label>Macro tipologia scarto *</Label>
              <Select value={form.macro_scrap_category} onValueChange={v => setForm({ ...form, macro_scrap_category: v })}>
                <SelectTrigger data-testid="scrap-macro"><SelectValue placeholder="Seleziona macro tipologia" /></SelectTrigger>
                <SelectContent>{MACRO_CATEGORIES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-3"><Label>Descrizione difetto *</Label>
              <Input value={form.defect_description} onChange={e => setForm({ ...form, defect_description: e.target.value })}
                placeholder="Es: porosità, misura fuori tolleranza, bava, difetto trattamento…" data-testid="scrap-defect-desc" />
            </div>
            <div className="col-span-2"><Label>Operatore / Reported by</Label><Input value={form.reported_by} onChange={e => setForm({ ...form, reported_by: e.target.value })} /></div>
            <div className="col-span-3"><Label>Note</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Annulla</Button>
            <Button onClick={save} data-testid="scrap-save">{editingId ? 'Salva modifiche' : 'Registra scarto'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
